import { hasLength } from "@-/com.npmjs.ts-array-length";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Strips every `columns` selection from an actor relational-query config — the
 * top-level one and, recursively, those inside `with` values. A nested `with`
 * entry whose config is empty after stripping is normalized to `true`, so
 * `{ rel: { columns: { a: true } } }` and `{ rel: true }` produce the same
 * stripped config and query-key hash.
 *
 * This makes actor-query cache entries column-agnostic: both the query key and
 * the fetch are built from the stripped config, so reads always return
 * full-width rows.
 */
export function stripRelationalConfigColumns<T>(config: T): T {
  if (!isPlainObject(config)) {
    return config;
  }
  const { columns: _columns, with: withClause, ...rest } = config;
  const stripped: Record<string, unknown> = rest;
  if (isPlainObject(withClause)) {
    const strippedWith: Record<string, unknown> = {};
    for (const [relationName, relationValue] of Object.entries(withClause)) {
      if (!isPlainObject(relationValue)) {
        strippedWith[relationName] = relationValue;
        continue;
      }
      const strippedRelation = stripRelationalConfigColumns(relationValue);
      strippedWith[relationName] = hasLength(Object.keys(strippedRelation), 0)
        ? true
        : strippedRelation;
    }
    stripped.with = strippedWith;
  } else if (withClause !== undefined) {
    stripped.with = withClause;
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- same config shape minus `columns`, which only widens the inferred rows
  return stripped as T;
}
